import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { artistName } from "../lib/artists";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const pad = (n) => String(n).padStart(2, "0");
const dateKey = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

export default function AdminCalendar({ bookings }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const events = useMemo(() => {
    const map = {};
    bookings.forEach((b) => {
      if (!b.scheduled_date) return;
      const done = b.status === "archived_done" || b.status === "archived";
      const scheduled = b.status === "scheduled";
      if (!done && !scheduled) return;
      const dates = b.scheduled_dates?.length ? b.scheduled_dates : [b.scheduled_date];
      dates.forEach((d, i) => {
        (map[d] = map[d] || []).push({
          id: b.id,
          name: dates.length > 1 ? `${b.name} (${i + 1}/${dates.length})` : b.name,
          artist: b.scheduled_artist || b.preferred_artist,
          done,
        });
      });
    });
    return map;
  }, [bookings]);

  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday-first
  const cells = [
    ...Array.from({ length: startOffset }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = firstDay.toLocaleString("en-AU", { month: "long", year: "numeric" });
  const isToday = (d) =>
    d && year === today.getFullYear() && month === today.getMonth() && d === today.getDate();

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear((y) => y - 1); } else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear((y) => y + 1); } else setMonth((m) => m + 1);
  };

  return (
    <div data-testid="admin-calendar" className="bg-white border border-[#E5E5E5]">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5]">
        <button
          onClick={prevMonth}
          data-testid="calendar-prev-month"
          className="p-2 hover:bg-[#FAFAFA] border border-[#E5E5E5]"
          aria-label="Previous month"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-center">
          <div
            data-testid="calendar-month-label"
            className="text-sm md:text-base tracking-[0.3em] uppercase"
          >
            {monthLabel}
          </div>
          <div className="mt-2 flex items-center justify-center gap-5 text-[9px] tracking-[0.2em] uppercase text-[#666]">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 bg-red-600 inline-block" /> Scheduled
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 bg-green-600 inline-block" /> Done
            </span>
          </div>
        </div>
        <button
          onClick={nextMonth}
          data-testid="calendar-next-month"
          className="p-2 hover:bg-[#FAFAFA] border border-[#E5E5E5]"
          aria-label="Next month"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-[#E5E5E5]">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-2 py-2 text-[9px] md:text-[10px] tracking-[0.25em] uppercase text-[#999] text-center">
            {w}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const key = d ? dateKey(year, month, d) : null;
          const dayEvents = key ? events[key] || [] : [];
          return (
            <div
              key={i}
              data-testid={key ? `calendar-day-${key}` : undefined}
              className={`min-h-[92px] md:min-h-[110px] border-b border-r border-[#F0F0F0] p-1.5 md:p-2 ${d ? "bg-white" : "bg-[#FAFAFA]"}`}
            >
              {d && (
                <>
                  <div
                    className={`text-[11px] md:text-xs mb-1 ${
                      isToday(d)
                        ? "inline-flex items-center justify-center w-6 h-6 bg-[#0A0A0A] text-white"
                        : "text-[#666]"
                    }`}
                  >
                    {d}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.map((ev) => (
                      <div
                        key={ev.id}
                        data-testid={`calendar-event-${ev.id}`}
                        title={`${ev.name} — ${artistName(ev.artist)} (${ev.done ? "Done" : "Scheduled"})`}
                        className={`px-1.5 py-1 text-[9px] md:text-[10px] leading-tight text-white truncate ${
                          ev.done ? "bg-green-600" : "bg-red-600"
                        }`}
                      >
                        <span className="font-medium">{ev.name}</span>
                        <span className="opacity-80"> · {artistName(ev.artist)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
